import {FC, useState} from "react";
import {Role, UserPost} from "../../../../types/domain.ts";
import {App, Form, FormProps, InputNumber, Modal, Segmented, Select} from "antd";
import {ROLE_OPTIONS} from "../../../../constants/constants.ts";
import {useFirs} from "../../../../queries/useFirs.ts";
import {useRegions} from "../../../../queries/useRegions.ts";
import {usersPost} from "../../../../api/users/usersPost.ts";

type Props = {
    open: boolean;
    onClose: VoidFunction;
}

type FormType = {
    id: number;
    role: Role;
    fir_ids?: string[];
    region_id?: number;
}

const makePostBody = (values: FormType): UserPost => {
    switch(values.role) {
        case 'fir_manager':
        case "fir_reader":
            if (!values.fir_ids?.length) throw new Error();

            return {
                id: values.id,
                role: values.role,
                fir_ids: values.fir_ids,
            };
        case 'region_manager':
            if(!values.region_id) throw new Error();

            return {
                id: values.id,
                role: values.role,
                region_id: values.region_id,
            };
        case 'staff':
            return {
                id: values.id,
                role: values.role,
            };
    }
}

export const AddUserModal: FC<Props> = ({ open, onClose }) => {
    const { message } = App.useApp();
    const [form] = Form.useForm<FormType>();
    const [loading, setLoading] = useState(false);

    const role = Form.useWatch('role', form);

    const { data: firs } = useFirs();
    const { data: regions } = useRegions();

    const handleFinish: FormProps<FormType>['onFinish'] = async (values) => {
        try {
            setLoading(true);
            const body = makePostBody(values);
            await usersPost(body);
            void message.success('Успешно. Пользователь появится в таблице после первой авторизации.');
            onClose();
        } catch(e) {
            void message.error(`Произошла ошибка при добавлении пользователя (${JSON.stringify(e)})`);
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal
            open={open}
            title="Добавить пользователя"
            onOk={form.submit}
            okButtonProps={{ loading }}
            onCancel={onClose}
            destroyOnClose
        >
            <Form<FormType>
                form={form}
                onFinish={handleFinish}
                layout="vertical"
                preserve={false}
                initialValues={{
                    role: 'fir_manager'
                }}
            >
                <Form.Item<FormType>
                    name="id"
                    label="CID"
                    rules={[{ type: "number", required: true }]}
                >
                    <InputNumber controls={false} />
                </Form.Item>
                <Form.Item<FormType>
                    name="role"
                    label="Роль"
                    rules={[{ type: 'enum', required: true, enum: ["fir_reader", "fir_manager", "region_manager", "staff"] }]}
                >
                    <Segmented options={ROLE_OPTIONS} />
                </Form.Item>
                {(role === 'fir_manager' || role === 'fir_reader') && (
                    <Form.Item<FormType>
                        name="fir_ids"
                        label="РПИ"
                        rules={[{ type: 'array', required: true }]}
                    >
                        <Select
                            options={firs?.map(({ id, name }) => ({
                                value: id,
                                label: `${name} (${id})`
                            }))}
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                )}
                {role === 'region_manager' && (
                    <Form.Item<FormType>
                        name="region_id"
                        label="Регион"
                        rules={[{ type: 'number', required: true }]}
                    >
                        <Select
                            options={regions?.map(({ id, name }) => ({
                                value: id,
                                label: name
                            }))}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    )
}